import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { setupI18n } from "@acto/web-common/locales";
import { createTaskpaneRouter } from "@acto/taskpane/core/router/router";
import translationEnUS from "@acto/taskpane/core/locales/en-US.json";
import translationDeDE from "@acto/taskpane/core/locales/de-DE.json";
import { DayjsLocaleProvider } from "@acto/taskpane/core/contexts/DayjsLocaleProvider";
import { bootPlatform } from "@acto/taskpane/core/platform";
import { PlatformBridgeProvider } from "@acto/taskpane/core/platform/messaging/bridge";
import { PlatformNameProvider } from "@acto/taskpane/core/platform/PlatformNameContext";
import { SessionTelemetryProvider } from "@acto/taskpane/core/telemetry/SessionTelemetryProvider";

import "@acto/taskpane/main.css";
import "@pigment-css/react/styles.css";

console.debug("[acto] widget-core build:", import.meta.env.VITE_BUILD_VERSION ?? "local");

async function setupLocales() {
  await setupI18n({ translationDeDE, translationEnUS });
}

function TaskpaneRouterHost({ persistSessionInFrame }: { persistSessionInFrame: boolean }) {
  const router = useMemo(() => createTaskpaneRouter({ persistSessionInFrame }), [persistSessionInFrame]);

  return <RouterProvider router={router} />;
}

export async function bootstrapTaskpaneApp() {
  const platform = await bootPlatform();

  const rootElement = document.getElementById("root");
  const root = rootElement ? createRoot(rootElement) : undefined;

  await setupLocales();

  if (!root) return;

  root.render(
    <StrictMode>
      <PlatformNameProvider platform={platform.plugin}>
        <SessionTelemetryProvider>
          <PlatformBridgeProvider bridge={platform.bridge}>
            <DayjsLocaleProvider>
              <TaskpaneRouterHost persistSessionInFrame={platform.persistSessionInFrame} />
            </DayjsLocaleProvider>
          </PlatformBridgeProvider>
        </SessionTelemetryProvider>
      </PlatformNameProvider>
    </StrictMode>,
  );
}
